/**
 * FT-565 — Generic list-field merge for shared-id records (dependency-free leaf).
 *
 * ── The bug this fixes ─────────────────────────────────────────────────────
 * ARRAY_COLLECTIONS sync unions items by `id`, but when the SAME id exists on
 * both sides (two devices edited the same record), the three merge sites
 * (pull `mergePullDelta`, bridge push union, outbox drain union) pick ONE WHOLE
 * ITEM as the winner by a single timestamp comparison
 * (`resolvePullMergeWinner` / `resolvePushMergeWinner`). Fields inside the item
 * are NOT merged. Live-reproduced: Device A adds a photo to an equipment
 * item's `photos` list while Device B edits a text field on the same item; B's
 * later timestamp wins the whole item and A's photo is silently discarded.
 *
 * ── What this module does ──────────────────────────────────────────────────
 * After a merge site has ALREADY chosen the whole-item winner, it calls
 * `mergeListFields(collection, winner, loser)`. For each list field configured
 * for that collection in `LIST_FIELD_MERGE`, the winner's list is kept and any
 * of the loser's elements whose key is NOT already in the winner's list are
 * appended. On a key clash the winner's element is kept. This is ADDITIVE ONLY:
 * it never re-decides which record body wins, and it never removes anything.
 *
 * Scope (deliberate): this rescues a list ADDITION when a competing plain-field
 * edit wins. It does NOT rescue a plain-field edit when the list change wins —
 * that needs per-field timestamps (FT-568, out of scope).
 *
 * ── Tombstone-aware fields (`respectTombstones: true`) ─────────────────────
 * A blind union of a list that supports deletion would RESURRECT deleted
 * elements (the loser device still holds the element; the union re-adds it).
 * Such lists must soft-delete elements with the FT-458 tombstone convention
 * (`deleted_at` marker kept IN the array — see ./tombstone.js) instead of
 * splicing them out. With `respectTombstones: true`:
 *   - a tombstoned element from EITHER side stays tombstoned: on a key clash,
 *     the loser's tombstone marker is re-applied onto the winner's element via
 *     `retainTombstone` (never un-tombstoned by the union);
 *   - a loser-only tombstoned element is carried across, so the deletion
 *     propagates.
 * `auditLog` is genuinely append-only (entries are never removed) so it does
 * not need this flag. `photos` does (users can delete a photo).
 *
 * ── Elements with no key ───────────────────────────────────────────────────
 * Loser elements with no usable key (e.g. legacy base64 STRING photo entries,
 * or a malformed object) are NOT appended: they cannot be de-duplicated or
 * tombstoned, so unioning them could duplicate or resurrect. This keeps the
 * pre-FT-565 behaviour (winner's list as-is) for those elements. The winner's
 * own keyless elements are always kept untouched.
 *
 * ── Import-cycle note (FT-331) ─────────────────────────────────────────────
 * This module's ONLY import is ./tombstone, which is itself a dependency-free
 * leaf (no imports at all). So both UniversalSyncBridge.js and lib/outbox.js
 * can import this file without recreating the
 * `UniversalSyncBridge → outbox → UniversalSyncBridge` cycle FT-331 broke.
 * Do NOT add imports of React / db / storage / the bridge here.
 *
 * ── Activating more collections / fields later ─────────────────────────────
 * Add an entry to LIST_FIELD_MERGE. Nothing else changes: all three merge sites
 * already call `mergeListFields` for every collection, and it is a no-op for
 * any collection/field not in the config. BEFORE activating a field, confirm
 * (a) the element key field, and (b) whether elements can be deleted — if they
 * can, the delete flow MUST tombstone instead of splice, and the field MUST set
 * `respectTombstones: true`, or the union will resurrect deletions.
 *
 * @module sync/mergeListFields
 */

import { isTombstoned, retainTombstone } from './tombstone';

/**
 * Which list fields, on which collections, are combined (union) instead of
 * taken wholesale from the whole-item winner.
 *
 * FT-565: ONLY `sr.equipment_records.v1` `photos` + `auditLog` are active.
 *  - photos:   PhotoRef elements keyed by `photoId` (typedef in
 *              components/common/PhotoAttachment/hooks/usePhotoStore.js:
 *              `{ photoId, capturedAt, capturedBy, caption? }` — NO `id`).
 *              Deletable → tombstoned in place by PhotoAttachment
 *              `tombstoneOnDelete` (set by maintenance/DocumentsTab.js).
 *  - auditLog: entries from utils/auditUtils.js `createAuditEntry`, keyed by
 *              `id` (`audit_<ms>_<rand>`). Append-only.
 *
 * Deliberately NOT activated (do not add without a separate ticket):
 *  - `serviceHistory` (FT-569 — separate, not-yet-understood bug);
 *  - `sr.operations_voyages.v1` `unifiedLog` (FT-482 has its own special case
 *    in mergePullDelta; deferred).
 *
 * Frozen so a stray runtime mutation can't silently activate a field.
 */
export const LIST_FIELD_MERGE = Object.freeze({
  'sr.equipment_records.v1': Object.freeze({
    photos: Object.freeze({ key: 'photoId', respectTombstones: true }),
    auditLog: Object.freeze({ key: 'id' }),
  }),
});

const keyOf = (element, keyField) => {
  if (!element || typeof element !== 'object') return undefined;
  const k = element[keyField];
  return k === undefined || k === null || k === '' ? undefined : k;
};

/**
 * Union one list field (winner-first). Returns `null` when the result would be
 * identical to the winner's list, so callers can avoid needless copies.
 *
 * @param {Array|undefined} winnerList
 * @param {Array|undefined} loserList
 * @param {{ key: string, respectTombstones?: boolean }} spec
 * @returns {Array|null}
 */
const unionList = (winnerList, loserList, spec) => {
  if (!Array.isArray(loserList) || loserList.length === 0) return null;
  const { key, respectTombstones } = spec;
  const base = Array.isArray(winnerList) ? winnerList : [];

  // Index winner elements by key so clashes can be detected (and, for
  // tombstone-aware fields, a loser tombstone re-applied in place).
  const indexByKey = new Map();
  base.forEach((el, idx) => {
    const k = keyOf(el, key);
    if (k !== undefined && !indexByKey.has(k)) indexByKey.set(k, idx);
  });

  let result = null; // lazily copied
  const ensureCopy = () => {
    if (!result) result = base.slice();
    return result;
  };

  loserList.forEach((loserEl) => {
    const k = keyOf(loserEl, key);
    if (k === undefined) return; // keyless → never unioned (see module header)
    if (indexByKey.has(k)) {
      // Key clash: the winner's element wins. For tombstone-aware fields, a
      // loser-side deletion must stay deleted (sticky — never un-tombstoned).
      if (respectTombstones && isTombstoned(loserEl)) {
        const idx = indexByKey.get(k);
        const current = (result || base)[idx];
        if (!isTombstoned(current)) {
          ensureCopy()[idx] = retainTombstone(loserEl, current);
        }
      }
      return;
    }
    // Loser-only element → append (a loser-only tombstone is carried as-is so
    // the deletion propagates; it is filtered from display by the UI).
    const out = ensureCopy();
    indexByKey.set(k, out.length);
    out.push(loserEl);
  });

  return result;
};

/**
 * Combine configured list fields of `loser` into `winner` (winner-first union).
 *
 * Call ONLY after the whole-item winner has been decided. Pure: never mutates
 * its inputs. Returns `winner` itself (same reference) when the collection has
 * no config, either side isn't an object, or nothing needed adding.
 *
 * @param {string} collection  sync collection name, e.g. 'sr.equipment_records.v1'
 * @param {Object} winner      the item the merge site's tie-break chose
 * @param {Object} loser       the other side's copy of the same id (may be nullish)
 * @returns {Object} winner, or a shallow copy with configured fields unioned
 */
export const mergeListFields = (collection, winner, loser) => {
  const config = collection ? LIST_FIELD_MERGE[collection] : undefined;
  if (!config) return winner;
  if (!winner || typeof winner !== 'object' || Array.isArray(winner)) return winner;
  if (!loser || typeof loser !== 'object' || Array.isArray(loser) || loser === winner) return winner;

  let merged = winner;
  Object.keys(config).forEach((field) => {
    const combined = unionList(winner[field], loser[field], config[field]);
    if (combined) {
      if (merged === winner) merged = { ...winner };
      merged[field] = combined;
    }
  });
  return merged;
};

const mergeListFieldsModule = { LIST_FIELD_MERGE, mergeListFields };
export default mergeListFieldsModule;
